import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, ChevronDown, Check, MapPin, Loader2 } from "lucide-react";
import axios from "axios";

const API = '/api';

// Auth Context for sharing auth state across commercial pages
const AuthContext = createContext({
  isAuthenticated: false,
  authLoading: true,
  user: null
});

export const useCommercialAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user came from login with state
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setAuthLoading(false);
      return;
    }

    // Skip auth check during OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setIsAuthenticated(true);
      setAuthLoading(false);
      return;
    }

    // Check auth status
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        navigate("/admin/login");
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, [navigate, location]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <AuthContext.Provider value={{ isAuthenticated, authLoading, user }}>
      {children}
    </AuthContext.Provider>
  );
};

// Site Context for sharing selected site across components
const SiteContext = createContext({
  selectedSite: "all",
  setSelectedSite: () => {},
  sites: [],
  loading: true
});

export const useSite = () => useContext(SiteContext);

export const SiteProvider = ({ children }) => {
  const [selectedSiteState, setSelectedSiteState] = useState(() =>
    localStorage.getItem("adminSelectedSite") || "all"
  );
  const selectedSite = selectedSiteState;
  const setSelectedSite = (siteId) => {
    localStorage.setItem("adminSelectedSite", siteId);
    setSelectedSiteState(siteId);
  };
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(Array.isArray(res.data) ? res.data : (res.data.sites || []));
    } catch (err) {
      console.error("Failed to fetch sites:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteContext.Provider value={{ selectedSite, setSelectedSite, sites, loading }}>
      {children}
    </SiteContext.Provider>
  );
};

// Combined Provider with Auth + Site
export const CommercialProvider = ({ children }) => (
  <AuthProvider>
    <SiteProvider>
      {children}
    </SiteProvider>
  </AuthProvider>
);

/**
 * Site Selector Component
 * Displays dropdown to filter by site across all commercial pages
 */
export const SiteSelector = ({ className = "" }) => {
  const { selectedSite, setSelectedSite, sites, loading } = useSite();
  const [open, setOpen] = useState(false);

  const selectedSiteData = sites.find(s => s.site_id === selectedSite);
  const displayName = selectedSite === "all" ? "All Sites" : (selectedSiteData?.name || "Select Site");

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-blue-300 transition-colors min-w-[180px]"
        disabled={loading}
      >
        <MapPin className="w-4 h-4 text-gray-500" />
        <span className="flex-1 text-left text-sm font-medium text-gray-700 truncate">
          {loading ? "Loading..." : displayName}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !loading && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 max-h-60 overflow-y-auto">
            {/* All Sites Option */}
            <button
              onClick={() => { setSelectedSite("all"); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${
                selectedSite === "all" ? "bg-blue-50 text-blue-700" : "text-gray-700"
              }`}
            >
              <Building2 className="w-4 h-4" />
              <span className="flex-1 text-left">All Sites</span>
              {selectedSite === "all" && <Check className="w-4 h-4" />}
            </button>

            {sites.length > 0 && (
              <div className="border-t border-gray-100 my-1" />
            )}

            {/* Individual Sites */}
            {sites.map((site) => (
              <button
                key={site.site_id}
                onClick={() => { setSelectedSite(site.site_id); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${
                  selectedSite === site.site_id ? "bg-blue-50 text-blue-700" : "text-gray-700"
                }`}
              >
                <MapPin className="w-4 h-4" />
                <div className="flex-1 text-left">
                  <p className="font-medium">{site.name}</p>
                  <p className="text-xs text-gray-500">{site.code}</p>
                </div>
                {selectedSite === site.site_id && <Check className="w-4 h-4" />}
              </button>
            ))}

            {sites.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-500 text-center">No sites configured</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SiteSelector;
